import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  DndContext, DragOverlay, useDroppable, useDraggable,
  PointerSensor, useSensor, useSensors, closestCorners,
} from '@dnd-kit/core';
import { recruitingApi } from '../../../api/recruiting.js';

const STAGES = ['applied', 'screen', 'interview', 'offer', 'hired', 'rejected'];

/**
 * Application pipeline Kanban. Drag a candidate card between stage columns to
 * move it along the pipeline (forward-only; the API rejects illegal jumps).
 * Mirrors the @dnd-kit pattern in pm/Board.jsx incl. the BUG-004 post-drag
 * click guard.
 */
export default function Pipeline() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const didDragRef = useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    recruitingApi
      .getPipeline(id)
      .then((d) => setData(d))
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(load, [load]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allApps = data ? data.stages.flatMap((s) => s.applications) : [];

  const onDragStart = useCallback((e) => { setActiveId(e.active.id); didDragRef.current = false; }, []);
  const onDragMove = useCallback(() => { didDragRef.current = true; }, []);
  const onDragEnd = useCallback(async (e) => {
    setActiveId(null);
    if (!e.over || !data) return;
    const appId = e.active.id;
    const overId = String(e.over.id);
    const targetStage = overId.startsWith('stagecol-') ? overId.slice('stagecol-'.length) : null;
    if (!targetStage) return;
    const app = allApps.find((a) => a.id === appId);
    if (!app || app.stage === targetStage) return;

    // Optimistic move.
    setData((prev) => ({
      ...prev,
      stages: prev.stages.map((s) => {
        if (s.key === targetStage) return { ...s, applications: [...s.applications, { ...app, stage: targetStage }] };
        return { ...s, applications: s.applications.filter((a) => a.id !== appId) };
      }),
    }));
    try {
      await recruitingApi.moveStage(appId, targetStage);
    } catch (err) {
      setError(err.response?.data?.message || 'Illegal stage move');
      load();
    }
  }, [data, allApps, load]);

  if (loading && !data) return <div className="muted" data-testid="recruiting-pipeline-page" data-loading="true">Loading…</div>;
  if (!data) return <div className="alert error">Pipeline not found</div>;

  const draggingApp = activeId ? allApps.find((a) => a.id === activeId) : null;

  return (
    <section data-testid="recruiting-pipeline-page" data-loading={loading ? 'true' : 'false'} data-loaded="true">
      <header className="page-header">
        <div>
          <h1>{data.requisition?.title} · Pipeline</h1>
          <p className="muted">Drag candidates between stages</p>
        </div>
        <Link to={`/recruiting/requisitions/${id}`} className="btn ghost">Back</Link>
      </header>
      {error && <div className="alert error" data-testid="recruiting-pipeline-error">{error}</div>}

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={onDragStart} onDragMove={onDragMove} onDragEnd={onDragEnd}>
        <div className="kanban" data-testid="recruiting-pipeline-board" style={{ overflowX: 'auto' }}>
          <div style={{ display: 'flex', gap: 12, minWidth: 'max-content' }}>
            {STAGES.map((stage) => {
              const col = data.stages.find((s) => s.key === stage) || { key: stage, applications: [] };
              return <Column key={stage} stage={stage} applications={col.applications} didDragRef={didDragRef} />;
            })}
          </div>
        </div>
        <DragOverlay>{draggingApp && <Card app={draggingApp} dragging />}</DragOverlay>
      </DndContext>
    </section>
  );
}

function Column({ stage, applications, didDragRef }) {
  const { isOver, setNodeRef } = useDroppable({ id: `stagecol-${stage}` });
  return (
    <div
      ref={setNodeRef}
      className={`kcol ${isOver ? 'kcol-over' : ''}`}
      data-testid={`recruiting-application-stage-${stage}`}
      data-column-status={stage}
      data-loaded="true"
    >
      <header data-testid="column-header"><span className="kcol-name">{stage}</span><span className="kcol-count">{applications.length}</span></header>
      <div className="kcol-body">
        {applications.map((app) => <DraggableCard key={app.id} app={app} didDragRef={didDragRef} />)}
        {applications.length === 0 && <div className="kcol-empty muted">—</div>}
      </div>
    </div>
  );
}

function DraggableCard({ app, didDragRef }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: app.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.4 : 1 } : {};
  const { role: _r, tabIndex: _t, ...dragAttrs } = attributes;
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`recruiting-pipeline-card-${app.id}`}
      data-application-id={app.id}
      data-stage={app.stage}
      {...dragAttrs}
      {...listeners}
    >
      <Card app={app} didDragRef={didDragRef} />
    </div>
  );
}

function Card({ app }) {
  return (
    <div className="issue-card">
      <div className="issue-title">{app.candidate_name}</div>
      <footer><span className="chip">{app.candidate_email}</span></footer>
    </div>
  );
}
